import {Injectable, signal} from "@angular/core";
import {App} from "@capacitor/app";
import {
  BehaviorSubject,
  Subscription,
  catchError,
  concat,
  filter,
  from,
  fromEvent,
  interval,
  map,
  merge,
  of,
  shareReplay,
  switchMap,
  tap,
} from "rxjs";
import {CacheService} from "../modules/shared/services/cache.service";
import {AppService} from "./app.service";
import {
  AppVersionInfo,
  MobileAppUpdate,
} from "../interfaces/appVersion.interface";
import {PlatformService} from "./platform.service";
import {isVersionBelow} from "../helpers/app-version-compare.helper";
import {environment} from "src/environments/environment";

@Injectable({
  providedIn: "root",
})
export class AppUpdateService {
  private readonly refreshTrigger$ = new BehaviorSubject<void>(undefined);
  private readonly subscriptions: Subscription[] = [];
  private readonly refreshIntervalMs = 5 * 60_000;
  private dismissedVersion: string | null = null;
  private readonly mobileUpdateState = signal<MobileAppUpdate | null>(null);
  public readonly mobileUpdate = this.mobileUpdateState.asReadonly();
  private readonly mobileUpdateCheckedState = signal(
    !PlatformService.isNativePlatform()
  );
  public readonly mobileUpdateChecked =
    this.mobileUpdateCheckedState.asReadonly();

  public readonly appVersion$ = concat(
    from(this.cacheService.getFromCache("app_version")).pipe(
      map((cachedVersion) => {
        if (!cachedVersion || typeof cachedVersion !== "string") {
          return null;
        }
        return {version: cachedVersion, changelog: ""} as AppVersionInfo;
      }),
      filter(
        (cachedVersion): cachedVersion is AppVersionInfo => !!cachedVersion
      )
    ),
    this.refreshTrigger$.pipe(
      switchMap(() =>
        this.appService.getAppVersion().pipe(
          // The cached app version has no mobile minimum/latest version information.
          tap((response) => this.updateMobileUpdate(response)),
          catchError((error) => {
            console.error("Unable to refresh app version:", error);
            return of({version: "0", changelog: ""});
          })
        )
      )
    )
  ).pipe(shareReplay({bufferSize: 1, refCount: true}));

  constructor(
    private readonly cacheService: CacheService,
    private readonly appService: AppService
  ) {
    if (PlatformService.isNativePlatform()) {
      void App.addListener("appStateChange", ({isActive}) => {
        if (isActive) {
          this.refresh();
        }
      });
    }

    this.subscriptions.push(
      merge(
        interval(this.refreshIntervalMs),
        fromEvent(window, "focus")
      ).subscribe(() => {
        this.refresh();
      })
    );
  }

  refresh(): void {
    this.refreshTrigger$.next();
    this.updateServiceWorkerRegistration();
  }

  public dismissMobileUpdate(): void {
    const update = this.mobileUpdate();
    if (!update || update.required) {
      return;
    }

    this.dismissedVersion = update.version;
    this.mobileUpdateState.set(null);
    try {
      localStorage.setItem(this.dismissedVersionKey, update.version);
    } catch {
      // Keep the dismissal in memory when persistent storage is unavailable.
    }
  }

  private get dismissedVersionKey(): string {
    return `app_update_dismissed_${PlatformService.getPlatform()}`;
  }

  private hasDismissedVersion(version: string): boolean {
    if (this.dismissedVersion === version) {
      return true;
    }
    try {
      return localStorage.getItem(this.dismissedVersionKey) === version;
    } catch {
      return false;
    }
  }

  private updateMobileUpdate(response: AppVersionInfo): void {
    // An unsuccessful check must not clear an already required update.
    if (!response.version || response.version === "0") {
      return;
    }
    this.mobileUpdateCheckedState.set(true);
    if (!PlatformService.isNativePlatform()) {
      this.mobileUpdateState.set(null);
      return;
    }

    const minimum = response.minSupportedAppVersion;
    if (
      typeof minimum === "string" &&
      isVersionBelow(environment.appVersion, minimum)
    ) {
      this.mobileUpdateState.set({required: true, version: minimum});
      return;
    }

    const platform = PlatformService.getPlatform();
    const latest =
      platform === "ios"
        ? response.latestIosAppVersion
        : response.latestAndroidAppVersion;
    const shouldOfferUpdate =
      typeof latest === "string" &&
      isVersionBelow(environment.appVersion, latest) &&
      !this.hasDismissedVersion(latest);
    this.mobileUpdateState.set(
      shouldOfferUpdate ? {required: false, version: latest} : null
    );
  }

  private updateServiceWorkerRegistration(): void {
    if (!("serviceWorker" in navigator)) {
      return;
    }

    navigator.serviceWorker
      .getRegistration()
      .then((registration) => registration?.update())
      .catch((error) => {
        console.warn("Service worker update check failed:", error);
      });
  }
}
